import { v4 as uuidv4 } from "uuid";
import { AmplitudeClient } from "./amplitudeClient";
import { browserLanguage } from "./browserLanguage";
// Load external scripts dynamically
const loadScript = (src, id, attributes = {}) => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      resolve();
      return;
    }

    // Check if script already exists
    if (id && document.getElementById(id)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    if (id) script.id = id;
    // Set before the script runs: Plausible reads data-domain on load.
    Object.entries(attributes).forEach(([name, value]) =>
      script.setAttribute(name, value)
    );

    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

    document.head.appendChild(script);
  });
};

// Amplitude: the same project as the apps (./index.ts), through the same
// client. Until October 2026 analytics.js sent everything to Segment, which
// forwarded it to Amplitude; the client keeps analytics.js's anonymous id as
// the device id and its user id, so Amplitude keeps the same users.
// Client-side API key, not a secret.
const AMPLITUDE_API_KEY = "ac22a3ab520efc37f5560e93c6429721";

// localStorage behind the client's async storage. A private window or blocked
// storage just means nothing is remembered.
const storage = {
  getItem: async (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      return null;
    }
  },
  setItem: async (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (err) {
      // not remembered
    }
  },
};

// analytics.js kept its ids JSON-encoded in localStorage and plain in cookies.
const analyticsJsId = (name) => {
  let value = null;
  try {
    value = window.localStorage.getItem(name);
  } catch (err) {
    // try the cookie
  }
  if (!value) {
    const cookie = document.cookie
      .split("; ")
      .find((c) => c.startsWith(name + "="));
    value = cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
  }
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "string" || typeof parsed === "number"
      ? String(parsed)
      : null;
  } catch (err) {
    return value;
  }
};

let amplitudeClient = null;
const amplitude = () => {
  if (amplitudeClient) return amplitudeClient;
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }
  // The website chat iframe (/support-embed) isn't an app visit.
  if (window.location.pathname.startsWith("/support-embed")) return null;
  amplitudeClient = new AmplitudeClient({
    apiKey: AMPLITUDE_API_KEY,
    storage,
    context: () => ({
      platform: "Web",
      language: navigator.language,
      user_agent: navigator.userAgent,
    }),
    app: { version: null, build: null },
    uuid: uuidv4,
    previousIdentity: () => ({
      deviceId: analyticsJsId("ajs_anonymous_id"),
      userId: analyticsJsId("ajs_user_id"),
    }),
    library: "eventlive-web/1",
    // keepalive: a batch sent as the tab closes still arrives.
    fetch: (input, init) => fetch(input, { ...init, keepalive: true }),
    log: (message, details) => console.log(message, details ?? ""),
  });
  window.addEventListener("pagehide", () => void amplitudeClient.flush());
  return amplitudeClient;
};

// Only flat values, as the apps send (index.ts filterNested): callers pass
// whole profiles.
const flat = (properties) => {
  const out = {};
  Object.entries(properties || {}).forEach(([key, value]) => {
    if (value === undefined || Number.isNaN(value)) return;
    if (typeof value !== "object") out[key] = value;
  });
  return out;
};

// analytics.js's page properties, without the query string (sign-in links
// carry codes there).
const pageProperties = (name) => {
  const { origin, pathname } = window.location;
  const properties = {
    name,
    path: pathname,
    title: document.title,
    url: origin + pathname,
  };
  if (document.referrer) properties.referrer = document.referrer.split(/[?#]/)[0];
  return properties;
};

// UTM parameters become user properties, as Segment's Amplitude destination
// set them.
const utmProperties = () => {
  const out = {};
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (/^utm_(source|medium|campaign|term|content)$/.test(key)) out[key] = value;
  });
  return Object.keys(out).length ? out : null;
};

// Initialize Plausible: our self-hosted instance (pulse.eventlive.pro), the
// same site as www.eventlive.pro; filter by hostname for the web app. The
// Privacy Policy says our analytics are self-hosted, so nothing goes to
// plausible.io. The pageview-props script adds the event-* attributes to
// every event: the browser's language, as evt.live and eventlive.pro send it
// (browserLanguage.js).
const initializePlausible = async () => {
  if (typeof window === "undefined") return;

  try {
    const { lang, full } = browserLanguage(
      navigator.languages?.length ? navigator.languages : [navigator.language]
    );
    await loadScript(
      "https://pulse.eventlive.pro/js/script.pageview-props.js",
      "plausible-script",
      {
        "data-domain": "eventlive.pro",
        "event-browser_lang": lang,
        "event-browser_lang_full": full,
      }
    );

    // Set up plausible queue
    window.plausible =
      window.plausible ||
      function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };
  } catch (error) {
    console.error("Failed to load Plausible:", error);
  }
};

// Initialize all analytics services
const initializeAnalytics = async () => {
  if (typeof window === "undefined") return;
  // The website chat iframe (/support-embed) isn't an app visit.
  if (window.location.pathname.startsWith("/support-embed")) return;

  try {
    // Initialize all services
    amplitude();
    await initializePlausible();

    console.log("Analytics services initialized successfully");
  } catch (error) {
    console.error("Failed to initialize analytics:", error);
  }
};

const analytics = {
  track: (event, properties) => {
    try {
      const client = amplitude();
      if (client) void client.track(event, flat(properties));

      // Plausible tracking
      if (window.plausible) {
        window.plausible(event, { props: properties });
      }
    } catch (err) {
      console.error("Analytics tracking error:", err);
    }
  },

  // Screens, named "Viewed <name> Page" as Segment's Amplitude destination
  // named them.
  page: (name) => {
    try {
      const client = amplitude();
      if (!client || !name) return;
      const utm = utmProperties();
      if (utm) void client.identify(null, utm);
      void client.track(`Viewed ${name} Page`, pageProperties(name));
    } catch (err) {
      console.error("Analytics page error:", err);
    }
  },

  identify: (id, attributes) => {
    try {
      const client = amplitude();
      if (client) void client.identify(id, flat(attributes));
    } catch (err) {
      console.error("Analytics identify error:", err);
    }
  },

  identifyAnon: (attributes) => {
    try {
      const client = amplitude();
      if (client) void client.identify(null, flat(attributes));
    } catch (err) {
      console.error("Analytics identifyAnon error:", err);
    }
  },

  reset: () => {
    try {
      const client = amplitude();
      if (client) void client.reset();
    } catch (err) {
      console.error("Analytics reset error:", err);
    }
  },

  // Initialize all analytics services
  initialize: initializeAnalytics,
};

// Auto-initialize when the module is imported in a browser environment
if (typeof window !== "undefined" && typeof document !== "undefined") {
  // Wait for DOM to be ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeAnalytics);
  } else {
    // DOM is already ready
    setTimeout(initializeAnalytics, 0);
  }
}

export default analytics;
